import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 4: "Search Challenge" (Master Spec Section 5), campaign
 * levels 31-40. Teaches: Linear vs Binary Search, selecting an appropriate algorithm, sorted vs unsorted data, comparison count, efficiency reasoning.
 * Algorithm(s) used in this world: binary-search, linear-search.
 */
export const world04SearchChallengeLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-031",
    world: 4,
    levelInWorld: 1,
    globalLevelNumber: 31,
    algorithm: "linear-search",
    values: [27, 8, 45, 3, 19, 36],
    target: 45,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Recognize unsorted data as the reason Linear Search is the right tool here.",
      "Practice choosing an algorithm based on whether the dataset is sorted, not by habit.",
    ],
    notes: "This dataset is not sorted, so Binary Search's halving trick isn't available -- Linear Search is the appropriate choice.",
  }),
  createCampaignLevel({
    id: "campaign-032",
    world: 4,
    levelInWorld: 2,
    globalLevelNumber: 32,
    algorithm: "binary-search",
    values: [3, 8, 19, 27, 36, 45],
    target: 45,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "See the exact same values as level 31, now sorted, searched with Binary Search instead.",
      "Compare directly: same target, same dataset size, a different number of comparisons.",
    ],
    notes: "Same six values as level 31 but sorted -- now Binary Search can safely eliminate half the range each step.",
  }),
  createCampaignLevel({
    id: "campaign-033",
    world: 4,
    levelInWorld: 3,
    globalLevelNumber: 33,
    algorithm: "linear-search",
    values: [27, 8, 45, 3, 19, 36],
    target: 50,
    targetPresent: false,
    isSorted: false,
    learningObjectives: [
      "Confirm not found still requires a full scan when data is unsorted.",
      "Reinforce that an unsorted dataset offers no shortcut, whatever the outcome.",
    ],
    notes: "50 is absent from this unsorted dataset -- Linear Search must check all six values to be sure.",
  }),
  createCampaignLevel({
    id: "campaign-034",
    world: 4,
    levelInWorld: 4,
    globalLevelNumber: 34,
    algorithm: "binary-search",
    values: [3, 8, 19, 27, 36, 45],
    target: 50,
    targetPresent: false,
    isSorted: true,
    learningObjectives: [
      "See the same not found outcome reached with far fewer comparisons once the data is sorted.",
      "Directly compare against level 33's cost on identical values.",
    ],
    notes: "Same values as level 33, sorted -- Binary Search rules out absence in only a couple of steps.",
  }),
  createCampaignLevel({
    id: "campaign-035",
    world: 4,
    levelInWorld: 5,
    globalLevelNumber: 35,
    algorithm: "linear-search",
    values: [52, 14, 71, 8, 39, 60, 23, 47],
    target: 47,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "See an unsorted dataset where the target happens to sit at the very end.",
      "Observe how this maximizes Linear Search's comparison count.",
    ],
    notes: "Target is last among 8 unsorted values -- the most expensive found case for Linear Search here.",
  }),
  createCampaignLevel({
    id: "campaign-036",
    world: 4,
    levelInWorld: 6,
    globalLevelNumber: 36,
    algorithm: "binary-search",
    values: [8, 14, 23, 39, 47, 52, 60, 71],
    target: 47,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "See the same worst-case-for-Linear-Search target found quickly once the data is sorted.",
      "Build the core Search Challenge insight: sortedness, not luck, determines efficiency.",
    ],
    notes: "Same eight values as level 35, sorted -- Binary Search reaches the same target in far fewer steps.",
  }),
  createCampaignLevel({
    id: "campaign-037",
    world: 4,
    levelInWorld: 7,
    globalLevelNumber: 37,
    algorithm: "linear-search",
    values: [18, 63, 4, 52, 29, 41, 7],
    target: 4,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Practice recognizing an unsorted dataset at a glance before choosing a strategy.",
      "Reinforce that Linear Search works correctly on any dataset, sorted or not -- it is just not always the fastest choice.",
    ],
    notes: "An ordinary unsorted search -- Linear Search always works here, efficient or not.",
  }),
  createCampaignLevel({
    id: "campaign-038",
    world: 4,
    levelInWorld: 8,
    globalLevelNumber: 38,
    algorithm: "binary-search",
    values: [4, 7, 18, 29, 41, 52, 63],
    target: 4,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "See a first-element target resolved with repeated LEFT choices on sorted data.",
      "Complete the direct comparison with level 37's unsorted version.",
    ],
    notes: "Same seven values as level 37, sorted -- the same target found via a very different path.",
  }),
  createCampaignLevel({
    id: "campaign-039",
    world: 4,
    levelInWorld: 9,
    globalLevelNumber: 39,
    algorithm: "linear-search",
    values: [33, 71, 12, 58, 4, 89, 22, 46, 17],
    target: 89,
    targetPresent: true,
    isSorted: false,
    learningObjectives: [
      "Apply the full Linear Search process to a larger, unsorted, real-feeling dataset.",
      "Count comparisons as a concrete measure of search cost, not just a pass/fail result.",
    ],
    notes: "A larger unsorted dataset -- Linear Search remains correct, at the cost of more comparisons.",
  }),
  createCampaignLevel({
    id: "campaign-040",
    world: 4,
    levelInWorld: 10,
    globalLevelNumber: 40,
    algorithm: "binary-search",
    values: [3, 8, 14, 19, 27, 33, 40, 52, 68, 85],
    target: 68,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Consolidate the Search Challenge: choose Binary Search whenever data is sorted, Linear Search otherwise.",
      "Reflect on why sortedness -- not dataset size alone -- is the deciding factor for efficiency.",
    ],
    notes: "World 4 capstone -- a sorted 10-value dataset where Binary Search's advantage is clearest.",
  }),
];
